import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deaneriesService } from '../services/deaneries.service';
import { Deanery } from '../types';
import { DeaneriesTable, DeaneriesToolbar } from '../components/deaneries/DeaneriesTable';
import { DeaneriesForm } from '../components/deaneries/DeaneriesForm';

export default function DeaneriesPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDeanery, setEditingDeanery] = useState<Deanery | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const { data: deaneriesData, isLoading } = useQuery({
    queryKey: ['deaneries', { page, limit, search }],
    queryFn: () => deaneriesService.getList({ page, limit, search }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Deanery, 'id' | 'createdAt' | 'updatedAt' | 'temples' | 'coordinator'>) => deaneriesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deaneries'] });
      setIsFormOpen(false);
      setEditingDeanery(null);
      setNotification({ message: 'Благочиние добавлено', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Deanery> }) => deaneriesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deaneries'] });
      setIsFormOpen(false);
      setEditingDeanery(null);
      setNotification({ message: 'Благочиние обновлено', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deaneriesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deaneries'] });
      setNotification({ message: 'Благочиние удалено', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const filteredDeaneries = deaneriesData?.data || [];
  const total = deaneriesData?.pagination?.total || 0;

  const handleCreate = () => { setEditingDeanery(null); setIsFormOpen(true); };
  const handleEdit = (deanery: Deanery) => { setEditingDeanery(deanery); setIsFormOpen(true); };
  const handleSubmit = async (data: any) => {
    if (editingDeanery) await updateMutation.mutateAsync({ id: editingDeanery.id, data });
    else await createMutation.mutateAsync(data);
  };
  const handleDelete = (id: number) => { if (window.confirm('Удалить благочиние?')) deleteMutation.mutate(id); };
  const handleCloseNotification = () => setNotification(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Благочиния</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Добавить благочиние</Button>
      </Box>
      <DeaneriesToolbar search={search} onSearchChange={(value) => { setSearch(value); setPage(1); }} />
      <DeaneriesTable deaneries={filteredDeaneries} loading={isLoading} total={total} page={page} limit={limit} onPageChange={setPage} onEdit={handleEdit} onDelete={handleDelete} />
      <DeaneriesForm open={isFormOpen} onClose={() => setIsFormOpen(false)} onSubmit={handleSubmit} initialData={editingDeanery as any} loading={createMutation.isPending || updateMutation.isPending} error={createMutation.error?.message || updateMutation.error?.message} />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
